import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Calendar } from "@/components/ui/calendar";
import { DateRange } from "react-day-picker";
import { FaCalendarAlt, FaChevronDown } from "react-icons/fa";

interface DateRangePickerProps {
  selectedDateStart: string | null;
  selectedDateEnd: string | null;
  onDateChange: (date: DateRange | undefined) => void;
}

export default function DateRangePicker({
  selectedDateStart,
  selectedDateEnd,
  onDateChange,
}: DateRangePickerProps) {
  const { i18n,t } = useTranslation();
  const locale = i18n.language;
  const [dateOpen, setDateOpen] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const dateContainerRef = useRef<HTMLDivElement>(null);

  // Date picker visibility functions
  function openDateList() {
    if (!dateOpen && dateContainerRef.current) {
      dateContainerRef.current.classList.remove("hidden");
      dateContainerRef.current.classList.add("flex");
      setDateOpen(true);
    }
  }

  function closeDateList() {
    if (dateOpen && dateContainerRef.current) {
      dateContainerRef.current.classList.add("hidden");
      dateContainerRef.current.classList.remove("flex");
      setDateOpen(false);
    }
  }

  // Handle date selection
  const handleDateSelect = (range: DateRange | undefined) => {
    setDate(range);
    onDateChange(range);
    
    // Only close the date picker when both dates are selected
    if (range?.from && range?.to) {
      closeDateList();
    }
  };

  // Close when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      closeDateList();
    }
  };

  useEffect(() => {
    if (dateOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line
  }, [dateOpen]);

  return (
    <div
      ref={containerRef}
      onClick={openDateList}
      className="relative w-fit bg-white"
    >
      <div className="flex w-fit cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-lg border px-2 py-2 text-sm md:gap-5 md:px-5 md:text-base">
        <div className="flex items-center gap-2">
          <span>
            <FaCalendarAlt />
          </span>
          <span className="font-semibold text-primary">
            {t("driver.OrderhistoryTimePicker")}
          </span>
        </div>
        <span className="text-slate-400">
          {selectedDateStart} - {selectedDateEnd}
        </span>
        <FaChevronDown className="text-sm text-slate-400" />
      </div>

      <div 
        ref={dateContainerRef} 
        className="absolute start-0 top-full z-50 mt-1 hidden rounded-md border bg-white shadow-lg"
      >
        <Calendar
          initialFocus
          toYear={new Date().getFullYear()}
          mode="range"
          selected={date}
          onSelect={handleDateSelect}
          numberOfMonths={2}
          disabled={{ after: new Date() }}
          defaultMonth={new Date(new Date().setMonth(new Date().getMonth() - 1))}
          className="rounded-lg border bg-white p-3 text-black dark:bg-white"
          classNames={{
            day_range_start:
              "bg-sidebar rounded-none text-skyblue hover:text-white hover:bg-sidebar",
            day_range_end:
              "bg-sidebar rounded-none text-skyblue hover:text-white hover:bg-sidebar",
            day_range_middle:
              "bg-skyblue rounded-none dark:bg-skyblue text-black hover:text-slate-900 hover:bg-skyblue",
            day_selected:
              "bg-black rounded-none text-black hover:bg-black hover:text-white",
            day_today:
              "border rounded-none border-sidebar text-black hover:text-slate-900 hover:bg-skyblue",
            nav_button_next: "bg-white dark:bg-white border-0",
            nav_button_previous: "bg-white dark:bg-white rotate-180 border-0",
          }}
          dir={locale === "ar" ? "rtl" : "ltr"}
        />
      </div>
    </div>
  );
}